/**
 * A `Wire` made of the page's WebSocket to our server.
 *
 * The third file of this shape, and the last: `server/socket.ts` is a
 * WebSocket seen from the server, `channel.ts` is a data channel between two
 * browsers, and this is a WebSocket seen from the page. Above all three the
 * game speaks `Wire` and cannot tell them apart, which is what lets the same
 * client join a world on our machine and a world on somebody's laptop
 * without knowing there is a difference.
 */
import type { Wire } from "../core/net/link.js";
import { encode, decode } from "../core/net/protocol.js";

/** How long to wait for a socket to come up before calling it unreachable. */
const PATIENCE_MS = 6000;

/**
 * Open one, or answer null.
 *
 * Null rather than a throw because everything in this game fails to single
 * player: a server that is not there is a disappointment, and being unable
 * to play at all because of it would be a bug.
 */
export function socketWire(url: string): Promise<Wire | null> {
  return new Promise((resolve) => {
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      resolve(null);
      return;
    }
    socket.binaryType = "arraybuffer";

    const wire: Wire = {
      onMessage: null, onChunk: null, onClose: null,
      open: () => socket.readyState === WebSocket.OPEN,
      send(message: object): void {
        if (socket.readyState !== WebSocket.OPEN) return;
        socket.send(encode(message as never));
      },
      sendEncoded(_message: object, text: string): void {
        if (socket.readyState !== WebSocket.OPEN) return;
        socket.send(text);
      },
      sendChunk(frame: ArrayBuffer): void {
        if (socket.readyState !== WebSocket.OPEN) return;
        socket.send(frame);
      },
      close(code?: number, why?: string): void {
        try {
          if (why === undefined) socket.close(code);
          else socket.close(code ?? 1000, why);
        } catch { /* already gone */ }
      },
    };

    socket.addEventListener("message", (event) => {
      if (event.data instanceof ArrayBuffer) {
        wire.onChunk?.(event.data);
        return;
      }
      if (typeof event.data !== "string") return;
      const message = decode(event.data);
      if (message !== null) wire.onMessage?.(message);
    });
    socket.addEventListener("close", () => { wire.onClose?.(); });

    let settled = false;
    const give = (result: Wire | null): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    };
    const timer = setTimeout(() => give(null), PATIENCE_MS);
    socket.addEventListener("open", () => give(wire));
    socket.addEventListener("error", () => {
      // After it opened, an error is a drop, and `onClose` is what says so.
      if (!settled) give(null);
      else wire.onClose?.();
    });
  });
}
